import type { Deal } from "@/types/deal";

/** Rows not updated for this long get a "stale" marker in meeting mode. */
export const STALE_AFTER_DAYS = 14;

export function isStaleRow(row: Deal, now: Date = new Date()): boolean {
  const updated = (row as unknown as { updated_date?: unknown }).updated_date;
  if (!updated) return false;
  const at = new Date(updated as string).getTime();
  if (Number.isNaN(at)) return false;
  return now.getTime() - at > STALE_AFTER_DAYS * 24 * 60 * 60 * 1000;
}

/** The user is the row's deal lead or support (ids compared as strings). */
export function isMyRow(
  row: Deal,
  userId: number | string | null | undefined,
): boolean {
  if (userId == null || userId === "") return false;
  const me = String(userId);
  const data = row as unknown as Record<string, unknown>;
  const idOf = (value: unknown, fallback: unknown) =>
    (value as { id?: unknown } | null | undefined)?.id ?? fallback;
  return [
    idOf(data.deal_lead, data.deal_lead_id),
    idOf(data.deal_support, data.deal_support_id),
  ].some((id) => id != null && String(id) === me);
}

/** A full-width row heading each status in meeting mode. */
export type StatusBandRow = {
  id: string;
  __band: true;
  statusName: string;
  dealCount: number;
  addonCount: number;
  totalGbp: number;
  /** Its rows are hidden (click the band to toggle). */
  collapsed: boolean;
};

export const isStatusBandRow = (row: unknown): row is StatusBandRow =>
  !!row &&
  typeof row === "object" &&
  (row as { __band?: unknown }).__band === true;

/** Closed statuses ("Closed", "Closed - Apollo") start collapsed in the Meeting view. */
export const isCollapsedByDefault = (statusName: string): boolean =>
  statusName.trim().toLowerCase().startsWith("closed");

/** The user's expand/collapse choices, by band id; others use the default. */
export type BandOverrides = Record<string, boolean>;

/**
 * Insert a band row before each status group (rows are already sorted by
 * status). Totals use the GBP fully-funded amount so currencies add up.
 */
export function withStatusBands(
  rows: Deal[],
  overrides: BandOverrides = {},
): Array<Deal | StatusBandRow> {
  const result: Array<Deal | StatusBandRow> = [];
  let band: StatusBandRow | null = null;
  for (const row of rows) {
    const status = (
      row as unknown as { status?: { id?: number; status?: string } }
    ).status;
    const key = status?.id != null ? String(status.id) : "none";
    if (!band || band.id !== `band-${key}`) {
      const id = `band-${key}`;
      const statusName = status?.status ?? "No status";
      band = {
        id,
        __band: true,
        statusName,
        dealCount: 0,
        addonCount: 0,
        totalGbp: 0,
        collapsed: overrides[id] ?? isCollapsedByDefault(statusName),
      };
      result.push(band);
    }
    const data = row as unknown as Record<string, unknown>;
    if (data.parent_deal_id != null) band.addonCount += 1;
    else band.dealCount += 1;
    const amount = Number(data.loan_amount_fully_funded_gbp);
    if (Number.isFinite(amount)) band.totalGbp += amount;
    if (!band.collapsed) result.push(row);
  }
  return result;
}

export function formatGbpShort(amount: number): string {
  if (!amount) return "£0";
  if (Math.abs(amount) >= 1e9) return `£${(amount / 1e9).toFixed(2)}bn`;
  if (Math.abs(amount) >= 1e6) return `£${(amount / 1e6).toFixed(1)}m`;
  if (Math.abs(amount) >= 1e3) return `£${Math.round(amount / 1e3)}k`;
  return `£${Math.round(amount)}`;
}

/** Grid `context` used by the status band rows. */
export type StatusBandContext = { toggleBand: (bandId: string) => void };

/** "6 deals · 2 add-ons · £142.0m" */
export function bandSummary(band: StatusBandRow): string {
  return [
    `${band.dealCount} deal${band.dealCount === 1 ? "" : "s"}`,
    band.addonCount > 0
      ? `${band.addonCount} add-on${band.addonCount === 1 ? "" : "s"}`
      : null,
    formatGbpShort(band.totalGbp),
  ]
    .filter(Boolean)
    .join(" · ");
}
